import { addDays, isFuture } from 'date-fns';
import { truncate } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import strip from 'strip';
import { useAuthContext } from 'AuthContext';
import LikeButton from 'components/buttons/Like';
import ShareButton from 'components/buttons/Share';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardChips from 'components/Card/Chips';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import CardSticker from 'components/CardSticker';
import HTMLFragment from 'components/HTMLFragment';
import HeroCarousel from 'containers/HeroCarousel';
import stopEvent from 'lib/stopEvent';
import useIsMobile from 'lib/useIsMobile';

const CommunityCard = ({
  community,
  compact,
  viewer,
  assetKey,
  assetTypeKey,
  handleLike,
  ...props
}) => {
  const { handleIntercepted, interceptAction } = useAuthContext();
  const mobile = useIsMobile();
  const [liking, setLiking] = useState(false);
  const [likeStatus, setLikeStatus] = useState({ liked: false, likeCount: 0 });

  useEffect(() => {
    setLikeStatus({ liked: community?.liked, likeCount: community?.likeCount });
  }, [community?.likeCount, community?.liked]);

  const content = useMemo(() => {
    if (compact) {
      if (community?.summary) {
        return community?.summary;
      }
      if (community?.description) {
        return truncate(community?.description, {
          length: 140,
          separator: ' ',
        });
      }
    } else if (community?.description) {
      return community?.description;
    }
    return null;
  }, [community, compact]);

  const like = useCallback(() => {
    setLiking(true);
    handleLike({
      variables: {
        isLiked: !likeStatus?.liked,
        id: community?.sponsorId ? community?.sponsorId : community?.id,
        model: 'sponsor',
      },
    }).then(likeRes => {
      if (likeRes?.data?.likeOrDislikeSponsor === true) {
        setLikeStatus({ liked: true, likeCount: likeStatus.likeCount + 1 });
      } else {
        setLikeStatus({ liked: false, likeCount: likeStatus.likeCount - 1 });
      }
      setLiking(false);
    });
  }, [community, likeStatus]);

  const onLike = useCallback(
    e => {
      stopEvent(e);
      if (viewer?.loggedIn) {
        like();
      } else {
        interceptAction(`like-${community?.id}`);
      }
    },
    [community, interceptAction, like, viewer?.loggedIn],
  );

  const showHeader = useMemo(() => compact || (!compact && !mobile), [
    compact,
    mobile,
  ]);

  const sticker = useMemo(() => {
    const backgroundColor = null;
    let label = null;
    if (community?.featured) {
      label = 'Featured';
    }
    if (community?.hasApplicableEvents) {
      label = "See What's New";
    }
    if (isFuture(addDays(community?.created, 7))) {
      label = 'Just Added';
    }
    return !compact && backgroundColor && label ? (
      <CardSticker backgroundColor={backgroundColor} label={label} />
    ) : null;
  }, [community, compact]);

  const actions = useMemo(
    () => (
      <CardActions noVerticalPadding={!compact}>
        <LikeButton
          disabled={liking}
          liked={likeStatus?.liked}
          likes={likeStatus?.likeCount}
          onClick={onLike}
          primary={!liking}
        />
        {!compact && mobile && (
          <ShareButton
            hashtags={['sponsored']}
            primary
            title={strip(community?.name)}
            iconStyle={{
              transform: 'scaleX(-1)',
            }}
          />
        )}
      </CardActions>
    ),
    [community?.name, compact, liking, mobile, onLike, likeStatus],
  );

  const body = useMemo(
    () => (
      <Fragment>
        <HeroCarousel
          assetKey={assetKey}
          assetTypeKey={assetTypeKey}
          playlist={community?.heroPlayList}
          compact={compact}
        />
        {!compact && mobile && actions}
        {showHeader && (
          <CardHeader
            title={(
              <HTMLFragment style={{ fontSize: 15, fontWeight: '700' }}>
                {community?.sponsorName}
              </HTMLFragment>
            )}
          />
        )}
        <CardText noTopPadding={showHeader}>
          <HTMLFragment style={{ fontSize: 16 }}>{content}</HTMLFragment>
        </CardText>
        {!compact && community?.categories?.length > 0 && (
          <CardChips
            items={community?.categories.map(category => category.name)}
            noTopPadding
          />
        )}
        {compact && (
          <Fragment>
            {actions}
            {sticker}
          </Fragment>
        )}
      </Fragment>
    ),
    [
      actions,
      community?.categories,
      community?.heroPlaylist,
      community?.name,
      compact,
      content,
      mobile,
      showHeader,
      sticker,
    ],
  );
  useEffect(() => {
    if (viewer?.loggedIn) {
      handleIntercepted(`like-${community?.id}`, like);
    }
  }, [community?.id, handleIntercepted, like, viewer?.loggedIn]);
  return <Card {...props}>{body}</Card>;
};

CommunityCard.propTypes = {
  community: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  viewer: PropTypes.object.isRequired,
  assetKey: PropTypes.string,
  assetTypeKey: PropTypes.string,
  handleLike: PropTypes.func,
};

export default CommunityCard;
